// ============================================================
// EventPlatform_Part3.jsx  –  Screens 11-14
// ============================================================
import React, { useState, useEffect } from "react";
import { Fetchexecutequery, Fetchheaderdata, Fetchupdatequery } from '../../../services/process/BRS1/brsService7';
import { CreateData } from '../../../services/process/process_data/process_data';
import {
  FileText, Bell, CheckCircle, Clock, Calendar, Users, MapPin,
  Search, Filter, Eye, Edit3, Trash2, Copy, BarChart2, Globe,
  Upload, Plus, X, AlertCircle, Check, Send, ArrowRight, Building,
  Award, Archive, Zap, List, Flag, ChevronRight, ChevronLeft,
  ChevronDown, ChevronUp, MoreVertical, Save, AlertTriangle,
  ThumbsUp, ThumbsDown, MessageSquare, RotateCcw, UserPlus,
  Activity, Target, TrendingUp, Settings, Download, RefreshCw,
  Star, PieChart, Hash, Shield, Layers, BookOpen, Paperclip,
  ExternalLink, Phone, Mail, Link,
} from "lucide-react";

// ── Shared design tokens ───────────────────────────────────────────────────
const C = {
  primary: "#14253d", accent: "#2563eb", accent2: "#0f766e",
  warn: "#d97706", danger: "#dc2626", success: "#16a34a",
  purple: "#7c3aed", bg: "#eef3f8", card: "#ffffff",
  border: "#d7e0ea", text: "#0f172a", muted: "#64748b", light: "#e8eef5",
};

const AppShell = ({ children, style = {} }) => (
  <div
    style={{
      minHeight: "100vh",
      width: "100%",
      background:
        "radial-gradient(circle at top left, rgba(37,99,235,.08), transparent 30%), radial-gradient(circle at top right, rgba(15,118,110,.08), transparent 26%), linear-gradient(180deg, #f7fafc 0%, #eef4fa 100%)",
      color: C.text,
      fontFamily: "'Segoe UI', sans-serif",
      ...style,
    }}
  >
    {children}
  </div>
);

const PageWrap = ({ children, wide = false, style = {} }) => (
  <div
    style={{
      width: "100%",
      maxWidth: wide ? 1520 : 1320,
      margin: "0 auto",
      padding: "24px 28px 32px",
      boxSizing: "border-box",
      ...style,
    }}
  >
    {children}
  </div>
);

// ── User credentials from sessionStorage (Production Mode) ──────────────────
const getUserCredentials = () => JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

/* MOCK CREDENTIALS - COMMENTED OUT FOR PRODUCTION
const MOCK_CREDENTIALS_CREATOR = {
  user_id: 101, user_name: "admin_user", tenants_id: 1,
  groups_id: 2, selectedentity: 10, role: "event_creator", display_name: "EduWorld Org",
};
*/

const statusColors = {
  applied: { bg: "#dbeafe", text: "#1d4ed8" }, under_review: { bg: "#fef3c7", text: "#92400e" },
  shortlisted: { bg: "#d1fae5", text: "#065f46" }, invited: { bg: "#ede9fe", text: "#5b21b6" },
  accepted: { bg: "#d1fae5", text: "#065f46" }, rejected: { bg: "#fee2e2", text: "#991b1b" },
  expired: { bg: "#f3f4f6", text: "#6b7280" }, draft: { bg: "#f3f4f6", text: "#6b7280" },
  pending: { bg: "#fef3c7", text: "#92400e" }, withdrawn: { bg: "#fee2e2", text: "#991b1b" },
  not_invited: { bg: "#f3f4f6", text: "#6b7280" }, completed: { bg: "#d1fae5", text: "#065f46" },
  registration_open: { bg: "#d1fae5", text: "#065f46" },
};

const Badge = ({ status, label }) => {
  const s = statusColors[status] || { bg: "#f3f4f6", text: "#6b7280" };
  return (
    <span style={{ background: s.bg, color: s.text, padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700, letterSpacing: .4, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {label || status?.replace(/_/g, " ")}
    </span>
  );
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: C.card, borderRadius: 18, border: `1px solid rgba(148,163,184,.22)`, boxShadow: "0 16px 40px rgba(15,23,42,.06)", ...style }}>{children}</div>
);

const SectionHeader = ({ title, sub, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
    <div>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: C.text, letterSpacing: -.2 }}>{title}</h2>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted, lineHeight: 1.45 }}>{sub}</p>}
    </div>
    {action}
  </div>
);

const StatCard = ({ label, value, icon: Icon, color, sub, delta }) => (
  <Card style={{ padding: "20px 22px", flex: 1, minWidth: 140 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 900, color: C.text, lineHeight: 1.15, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{sub}</div>}
        {delta !== undefined && (
          <div style={{ fontSize: 11, color: delta >= 0 ? C.success : C.danger, marginTop: 4, fontWeight: 700 }}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}% vs last event
          </div>
        )}
      </div>
      <div style={{ background: color + "14", borderRadius: 14, padding: 10, flexShrink: 0 }}>
        <Icon size={22} color={color} />
      </div>
    </div>
  </Card>
);

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const base = { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 10, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", border: "none", transition: "all .15s", opacity: disabled ? .6 : 1, ...style };
  const variants = {
    primary: { background: C.accent, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    secondary: { background: C.light, color: C.primary, padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    danger: { background: C.danger, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    success: { background: C.success, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    ghost: { background: "transparent", color: C.accent, padding: size === "sm" ? "4px 10px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    outline: { background: "transparent", color: C.primary, border: `1.5px solid ${C.border}`, padding: size === "sm" ? "5px 13px" : "9px 19px", fontSize: size === "sm" ? 12 : 14 },
    warn: { background: C.warn, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
    purple: { background: C.purple, color: "#fff", padding: size === "sm" ? "6px 14px" : "10px 20px", fontSize: size === "sm" ? 12 : 14 },
  };
  return (
    <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>
      {Icon && <Icon size={size === "sm" ? 13 : 15} />}
      {children}
    </button>
  );
};

const Input = ({ ...props }) => (
  <input {...props} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: "#fff", outline: "none", boxSizing: "border-box", ...props.style }} />
);

const Select = ({ children, ...props }) => (
  <select {...props} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: "#fff", outline: "none", boxSizing: "border-box", ...props.style }}>
    {children}
  </select>
);

const BarChart = ({ data, color = C.accent, height = 80 }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.muted }}>{d.value > 0 ? d.value : ""}</div>
          <div style={{ width: "100%", height: `${Math.max((d.value / max) * (height - 20), 4)}px`, background: color, borderRadius: "4px 4px 0 0", transition: "height .4s" }} />
          <span style={{ fontSize: 9, color: C.muted, textAlign: "center", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const DonutChart = ({ segments, size = 100, label }) => {
  const r = 38, cx = 50, cy = 50;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.light} strokeWidth={14} />
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * circumference;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color}
            strokeWidth={14} strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={-offset}
            style={{ transform: "rotate(-90deg)", transformOrigin: "center" }} />
        );
        offset += dash;
        return el;
      })}
      {label && (
        <>
          <text x="50" y="47" textAnchor="middle" fontSize="13" fontWeight="900" fill={C.text}>{label.value}</text>
          <text x="50" y="59" textAnchor="middle" fontSize="8" fill={C.muted}>{label.sub}</text>
        </>
      )}
    </svg>
  );
};

const EmptyState = ({ icon: Icon = FileText, title, sub, action }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "50px 24px", gap: 12, textAlign: "center" }}>
    <div style={{ background: C.light, borderRadius: 16, padding: 18 }}><Icon size={28} color={C.muted} /></div>
    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{title}</div>
    <div style={{ fontSize: 13, color: C.muted, maxWidth: 260 }}>{sub}</div>
    {action}
  </div>
);

const ProgressBar = ({ value, color = C.accent, height = 7 }) => (
  <div style={{ background: C.light, borderRadius: 99, height, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", background: color, borderRadius: 99, transition: "width .5s" }} />
  </div>
);

// ── Mock data - COMMENTED OUT FOR PRODUCTION ──────────────────
/*
const MOCK_APPLICANTS = [...];
const MOCK_INVITATION_MGMT = [...];

// Using production services: Fetchexecutequery, Fetchheaderdata, Fetchupdatequery, CreateData
*/
const MOCK_APPLICANTS = [];

const FALLBACK_APPLICANTS = [
  {
    id: 1,
    school_name: "St. Xavier's High School",
    city: "Mumbai",
    country: "India",
    school_type: "Private",
    application_status: "shortlisted",
    applied_on: "2025-08-12",
    completion: 92,
    rep1_name: "Priya Sharma",
    rep1_email: "priya@stxaviers.edu.in",
    rep1_contact: "+91-9876543210",
    prev_participations: 3,
  },
  {
    id: 2,
    school_name: "Cambridge Prep School",
    city: "London",
    country: "UK",
    school_type: "International",
    application_status: "under_review",
    applied_on: "2025-08-16",
    completion: 84,
    rep1_name: "Emma Thompson",
    rep1_email: "emma@cambridgeprep.edu",
    rep1_contact: "+44-20-5555-0123",
    prev_participations: 2,
  },
];

const MOCK_INVITATION_MGMT = [
  { id: 1, school_name: "St. Xavier's High School", country: "India", rep: "Priya Sharma", invitation_date: "2025-08-25", acceptance_deadline: "2025-09-10", invitation_status: "accepted", event_id: 1 },
  { id: 2, school_name: "Cambridge Prep School", country: "UK", rep: "Emma Thompson", invitation_date: "2025-08-25", acceptance_deadline: "2025-09-10", invitation_status: "pending", event_id: 1 },
  { id: 3, school_name: "GreenLeaf International", country: "UAE", rep: "Ahmed Al-Rashid", invitation_date: "2025-08-26", acceptance_deadline: "2025-09-10", invitation_status: "pending", event_id: 1 },
  { id: 4, school_name: "Sunrise Academy", country: "Singapore", rep: "Li Wei", invitation_date: "2025-08-26", acceptance_deadline: "2025-09-10", invitation_status: "rejected", event_id: 1 },
  { id: 5, school_name: "Horizon Global School", country: "Australia", rep: "James O'Brien", invitation_date: "2025-08-27", acceptance_deadline: "2025-09-10", invitation_status: "expired", event_id: 1 },
];

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 11 – APPLICANT REVIEW PAGE
// ══════════════════════════════════════════════════════════════════════════
export function ApplicantReview() {
  const uc = { ...getUserCredentials(), role: "event_creator" };
  const applicantsSeed = MOCK_APPLICANTS.length ? MOCK_APPLICANTS : FALLBACK_APPLICANTS;
  const [selected, setSelected] = useState(applicantsSeed[0]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [note, setNote] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [applicants, setApplicants] = useState(applicantsSeed);

  // SELECT a.*, sp.* FROM event_platform.applications a
  // JOIN event_platform.school_profiles sp ON a.profile_id = sp.id
  // JOIN event_platform.events e ON a.event_id = e.id
  // WHERE e.creator_user_id = ${uc.user_id} AND e.creator_entity_id = ${uc.selectedentity}
  // AND e.tenant_id = ${uc.tenants_id} AND e.id = ${selectedEventId}

  const filtered = applicants.filter(a =>
    (!search || a.school_name.toLowerCase().includes(search.toLowerCase()) || a.country.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || a.application_status === statusFilter)
  );

  const handleAction = (id, action) => {
    setActionLoading(action);
    // UPDATE event_platform.applications SET application_status='${action}', last_updated_on=NOW(), modified_by=${uc.user_id}
    // WHERE id=${id} AND event_id IN (SELECT id FROM event_platform.events WHERE creator_user_id=${uc.user_id} AND creator_entity_id=${uc.selectedentity})
    setTimeout(() => {
      setApplicants(p => p.map(a => a.id === id ? { ...a, application_status: action } : a));
      setSelected(p => ({ ...p, application_status: action }));
      setActionLoading(null);
    }, 700);
  };

  const handleSaveNote = (appId) => {
    if (!note.trim()) return;
    // INSERT INTO event_platform.reviewer_notes (application_id, event_id, reviewer_user_id, note_text, flag, created_on)
    // VALUES (${appId}, ${eventId}, ${uc.user_id}, '${note}', 'none', NOW())
    alert("Note saved!");
    setNote("");
  };

  const ProfileSection = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.light}` }}>{title}</div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.light}`, fontSize: 13 }}>
      <span style={{ color: C.muted, fontWeight: 600 }}>{label}</span>
      <span style={{ color: C.text, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{value || "—"}</span>
    </div>
  );

  return (
    <AppShell>
      <PageWrap wide style={{ paddingTop: 0, paddingBottom: 0, maxWidth: "none" }}>
      <SectionHeader
        title="Applicant Review"
        sub={`Global EdTech Summit 2025 · Creator #${uc.user_id} · Entity #${uc.selectedentity} · ${applicants.length} applicants`}
        action={<Btn variant="outline" size="sm" icon={Download}>Export List</Btn>}
      />

      <div style={{ display: "flex", minHeight: "calc(100vh - 88px)" }}>
        {/* LEFT PANEL — Applicant list */}
        <div style={{ width: 340, flexShrink: 0, borderRight: `1px solid ${C.border}`, background: "#fff", display: "flex", flexDirection: "column", boxShadow: "inset -1px 0 0 rgba(148,163,184,.16)" }}>
          {/* Search + Filter */}
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search schools..." style={{ paddingLeft: 30, fontSize: 13 }} />
            </div>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 13 }}>
              <option value="">All Statuses</option>
              {["applied","under_review","shortlisted","rejected"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </Select>
          </div>

          {/* Count row */}
          <div style={{ padding: "8px 16px", background: C.light, fontSize: 11, fontWeight: 700, color: C.muted, display: "flex", justifyContent: "space-between" }}>
            <span>{filtered.length} results</span>
            <span>Sorted by: Applied Date</span>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {filtered.map(ap => (
              <div key={ap.id} onClick={() => setSelected(ap)}
                style={{ padding: "14px 16px", borderBottom: `1px solid ${C.light}`, cursor: "pointer", background: selected?.id === ap.id ? C.light : "transparent", transition: "background .15s" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text, lineHeight: 1.3, flex: 1, paddingRight: 8 }}>{ap.school_name}</div>
                  <Badge status={ap.application_status} />
                </div>
                <div style={{ fontSize: 11, color: C.muted, display: "flex", gap: 8 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 3 }}><MapPin size={9} />{ap.city}, {ap.country}</span>
                  <span>·</span>
                  <span>{ap.applied_on}</span>
                </div>
                <div style={{ marginTop: 6 }}>
                  <div style={{ height: 4, background: C.light, borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${ap.completion}%`, background: ap.completion > 80 ? C.success : ap.completion > 60 ? C.warn : C.danger, borderRadius: 99 }} />
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Profile {ap.completion}% complete</div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <EmptyState title="No applicants" sub="Adjust filters to see results" />}
          </div>
        </div>

        {/* RIGHT PANEL — Applicant details */}
        <div style={{ flex: 1, overflowY: "auto", background: C.bg }}>
          {selected ? (
            <div style={{ padding: "24px 28px" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div style={{ width: 52, height: 52, background: C.primary, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 20 }}>
                    {selected.school_name[0]}
                  </div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: C.text }}>{selected.school_name}</h2>
                    <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{selected.city}, {selected.country} · {selected.school_type}</div>
                    <div style={{ marginTop: 6 }}><Badge status={selected.application_status} /></div>
                  </div>
                </div>
                {/* Action buttons */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <Btn variant="success" size="sm" icon={Check} onClick={() => handleAction(selected.id, "shortlisted")} disabled={!!actionLoading || selected.application_status === "shortlisted"}>
                    {actionLoading === "shortlisted" ? "..." : "Shortlist"}
                  </Btn>
                  <Btn variant="danger" size="sm" icon={X} onClick={() => handleAction(selected.id, "rejected")} disabled={!!actionLoading || selected.application_status === "rejected"}>
                    {actionLoading === "rejected" ? "..." : "Reject"}
                  </Btn>
                  <Btn variant="warn" size="sm" icon={Flag} onClick={() => handleAction(selected.id, "under_review")} disabled={!!actionLoading}>
                    Mark for Review
                  </Btn>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* School Info */}
                <Card style={{ padding: 20 }}>
                  <ProfileSection title="School Information">
                    <InfoRow label="School Name" value={selected.school_name} />
                    <InfoRow label="Type" value={selected.school_type} />
                    <InfoRow label="City" value={selected.city} />
                    <InfoRow label="Country" value={selected.country} />
                    <InfoRow label="Profile Completion" value={`${selected.completion}%`} />
                  </ProfileSection>
                  <div style={{ marginTop: 8 }}>
                    <ProgressBar value={selected.completion} color={selected.completion > 80 ? C.success : C.warn} />
                  </div>
                </Card>

                {/* Representative Info */}
                <Card style={{ padding: 20 }}>
                  <ProfileSection title="Primary Representative">
                    <InfoRow label="Name" value={selected.rep1_name} />
                    <InfoRow label="Email" value={selected.rep1_email} />
                    <InfoRow label="Contact" value={selected.rep1_contact} />
                    <InfoRow label="Applied On" value={selected.applied_on} />
                    <InfoRow label="Previous Participations" value={selected.prev_participations} />
                  </ProfileSection>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <Btn variant="outline" size="sm" icon={Mail}>Email Rep</Btn>
                    <Btn variant="outline" size="sm" icon={Phone}>Call</Btn>
                  </div>
                </Card>

                {/* Documents */}
                <Card style={{ padding: 20 }}>
                  <ProfileSection title="Uploaded Documents">
                    {[
                      { name: "School_Brochure.pdf", size: "2.4 MB", type: "Brochure" },
                      { name: "Accreditation_2024.pdf", size: "1.1 MB", type: "Accreditation" },
                      { name: "Logo_HighRes.png", size: "340 KB", type: "Logo" },
                    ].map((doc, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.light}` }}>
                        <div style={{ background: C.accent + "20", borderRadius: 6, padding: "4px 8px", fontSize: 10, fontWeight: 800, color: C.accent }}>{doc.type.toUpperCase()}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{doc.name}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>{doc.size}</div>
                        </div>
                        <Btn variant="ghost" size="sm" icon={Download} style={{ padding: "3px 8px" }}></Btn>
                      </div>
                    ))}
                  </ProfileSection>
                </Card>

                {/* Reviewer Notes */}
                <Card style={{ padding: 20 }}>
                  <ProfileSection title="Reviewer Notes">
                    <textarea value={note} onChange={e => setNote(e.target.value)}
                      placeholder="Add a private note about this applicant..."
                      style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, minHeight: 90, resize: "vertical", boxSizing: "border-box", marginBottom: 10 }} />
                    <Btn variant="outline" size="sm" icon={Save} onClick={() => handleSaveNote(selected.id)}>Save Note</Btn>
                  </ProfileSection>
                  {/* Previous notes */}
                  <div style={{ marginTop: 8 }}>
                    {[{ text: "Strong profile, diverse student base. Recommend shortlisting.", date: "2025-08-22", reviewer: "Admin" }].map((n, i) => (
                      <div key={i} style={{ padding: "10px 12px", background: C.light, borderRadius: 8, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{n.text}</div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{n.reviewer} · {n.date}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          ) : (
            <EmptyState icon={Users} title="Select an applicant" sub="Click on an applicant in the list to view their full profile" />
          )}
        </div>
      </div>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 12 – INVITATION MANAGEMENT PAGE
// ══════════════════════════════════════════════════════════════════════════
export function InvitationManagement() {
  const uc = { ...getUserCredentials(), role: "event_creator" };
  const [invitations, setInvitations] = useState(MOCK_INVITATION_MGMT);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const shortlistSeed = MOCK_APPLICANTS.length ? MOCK_APPLICANTS : FALLBACK_APPLICANTS;

  // SELECT i.*, sp.school_name, sp.country, sp.rep1_name
  // FROM event_platform.invitations i
  // JOIN event_platform.applications a ON i.application_id = a.id
  // JOIN event_platform.school_profiles sp ON a.profile_id = sp.id
  // JOIN event_platform.events e ON i.event_id = e.id
  // WHERE e.creator_user_id = ${uc.user_id} AND e.creator_entity_id = ${uc.selectedentity} AND e.tenant_id = ${uc.tenants_id}

  const filtered = invitations.filter(inv =>
    (!search || inv.school_name.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || inv.invitation_status === statusFilter)
  );

  const statusCounts = invitations.reduce((acc, i) => { acc[i.invitation_status] = (acc[i.invitation_status] || 0) + 1; return acc; }, {});

  const handleInvAction = (id, action) => {
    setActionLoading({ id, action });
    setTimeout(() => {
      setInvitations(p => p.map(inv => inv.id === id ? { ...inv, invitation_status: action === "withdraw" ? "withdrawn" : inv.invitation_status } : inv));
      setActionLoading(null);
    }, 700);
  };

  return (
    <AppShell>
      <PageWrap style={{ maxWidth: 1400 }}>
        <SectionHeader
          title="Invitation Management"
          sub={`Global EdTech Summit 2025 · Creator #${uc.user_id} · Entity #${uc.selectedentity} · ${invitations.length} invitations issued`}
          action={<Btn variant="outline" size="sm" icon={Plus} onClick={() => setShowSendModal(true)}>Send New Invitation</Btn>}
        />

        {/* Status summary */}
        <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
          {[
            { label: "Total Sent", value: invitations.length, color: C.accent, icon: Send },
            { label: "Pending", value: statusCounts.pending || 0, color: C.warn, icon: Clock },
            { label: "Accepted", value: statusCounts.accepted || 0, color: C.success, icon: CheckCircle },
            { label: "Rejected", value: statusCounts.rejected || 0, color: C.danger, icon: X },
            { label: "Expired", value: statusCounts.expired || 0, color: C.muted, icon: AlertCircle },
          ].map(s => (
            <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} color={s.color} />
          ))}
        </div>

        {/* Filters */}
        <Card style={{ padding: "12px 16px", marginBottom: 20, display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by school name..." style={{ paddingLeft: 30, fontSize: 13 }} />
          </div>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 180 }}>
            <option value="">All Statuses</option>
            {["pending","accepted","rejected","expired","withdrawn"].map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Btn variant="outline" size="sm" icon={Download}>Export</Btn>
        </Card>

        {/* Table */}
        <Card style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.light }}>
                  {["School / Applicant", "Country", "Representative", "Invited On", "Acceptance Deadline", "Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: .4, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv, i) => {
                  const isLoading = actionLoading?.id === inv.id;
                  return (
                    <tr key={inv.id} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "#fff" : C.bg + "88" }}>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{inv.school_name}</div>
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: 13, color: C.muted }}>{inv.country}</td>
                      <td style={{ padding: "14px 16px", fontSize: 13, color: C.muted }}>{inv.rep}</td>
                      <td style={{ padding: "14px 16px", fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{inv.invitation_date}</td>
                      <td style={{ padding: "14px 16px", fontSize: 12, color: inv.invitation_status === "pending" ? C.warn : C.muted, fontWeight: inv.invitation_status === "pending" ? 700 : 400 }}>
                        {inv.acceptance_deadline}
                      </td>
                      <td style={{ padding: "14px 16px" }}><Badge status={inv.invitation_status} /></td>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {inv.invitation_status === "pending" && (
                            <Btn variant="outline" size="sm" icon={RefreshCw} onClick={() => handleInvAction(inv.id, "resend")} disabled={isLoading}>
                              {isLoading && actionLoading.action === "resend" ? "..." : "Resend"}
                            </Btn>
                          )}
                          {["pending", "accepted"].includes(inv.invitation_status) && (
                            <Btn variant="danger" size="sm" icon={X} onClick={() => handleInvAction(inv.id, "withdraw")} disabled={isLoading}>
                              {isLoading && actionLoading.action === "withdraw" ? "..." : "Withdraw"}
                            </Btn>
                          )}
                          {!["pending", "accepted"].includes(inv.invitation_status) && (
                            <Btn variant="purple" size="sm" icon={Send} onClick={() => setShowSendModal(true)}>
                              Re-invite
                            </Btn>
                          )}
                          <Btn variant="ghost" size="sm" icon={Eye}></Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <EmptyState icon={Send} title="No invitations found" sub="No invitations match the current filters" />}
          </div>
        </Card>
      </PageWrap>

      {/* Send Invitation Modal */}
      {showSendModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
          <Card style={{ width: 480, padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.text }}>Send Invitation</h3>
              <button onClick={() => setShowSendModal(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} color={C.muted} /></button>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.text, display: "block", marginBottom: 6 }}>Select Shortlisted Applicant</label>
              <Select>
                <option>-- Choose applicant --</option>
                {shortlistSeed.filter(a => a.application_status === "shortlisted").map(a => (
                  <option key={a.id}>{a.school_name} — {a.country}</option>
                ))}
              </Select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.text, display: "block", marginBottom: 6 }}>Acceptance Deadline</label>
              <Input type="date" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.text, display: "block", marginBottom: 6 }}>Custom Message (optional)</label>
              <textarea placeholder="Add a personal note to this invitation..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, minHeight: 80, resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="outline" onClick={() => setShowSendModal(false)}>Cancel</Btn>
              <Btn variant="primary" icon={Send} onClick={() => { alert("Invitation sent!"); setShowSendModal(false); }}>Send Invitation</Btn>
            </div>
          </Card>
        </div>
      )}
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 13 – EVENT ANALYTICS PAGE
// ══════════════════════════════════════════════════════════════════════════
export function EventAnalytics() {
  const uc = { ...getUserCredentials(), role: "event_creator" };

  // All queries filtered by creator_user_id = ${uc.user_id} AND creator_entity_id = ${uc.selectedentity}

  const geoData = [
    { country: "India", count: 42, pct: 48 }, { country: "UAE", count: 18, pct: 21 },
    { country: "UK", count: 12, pct: 14 }, { country: "Singapore", count: 9, pct: 10 },
    { country: "Australia", count: 6, pct: 7 },
  ];

  const trendData = [
    { label: "W1", value: 12 }, { label: "W2", value: 24 }, { label: "W3", value: 19 },
    { label: "W4", value: 32 }, { label: "W5", value: 87 },
  ];

  const typeData = [
    { label: "Private", value: 38, color: C.accent },
    { label: "Govt", value: 25, color: C.success },
    { label: "Intl", value: 15, color: C.purple },
    { label: "Aided", value: 9, color: C.warn },
  ];

  const funnelSteps = [
    { label: "Total Applicants", value: 87, color: C.accent, pct: 100 },
    { label: "Under Review", value: 72, color: C.purple, pct: 83 },
    { label: "Shortlisted", value: 60, color: C.warn, pct: 69 },
    { label: "Invited", value: 45, color: C.accent2, pct: 52 },
    { label: "Accepted", value: 38, color: C.success, pct: 44 },
  ];

  const topSchools = [
    { name: "Cambridge Prep School", country: "UK", events: 4, status: "accepted" },
    { name: "St. Xavier's High School", country: "India", events: 3, status: "accepted" },
    { name: "GreenLeaf International", country: "UAE", events: 2, status: "invited" },
    { name: "Sunrise Academy", country: "Singapore", events: 2, status: "accepted" },
    { name: "Delhi Public School", country: "India", events: 1, status: "shortlisted" },
  ];

  const [activeEvent, setActiveEvent] = useState("Global EdTech Summit 2025");

  return (
    <AppShell>
      <PageWrap wide>
        <SectionHeader
          title="Event Analytics"
          sub={`Creator #${uc.user_id} · Entity #${uc.selectedentity} — Your events only`}
          action={
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Select value={activeEvent} onChange={e => setActiveEvent(e.target.value)} style={{ width: 260, fontSize: 13 }}>
                {["Global EdTech Summit 2025", "STEM Innovation Expo", "School Leaders Forum"].map(e => <option key={e}>{e}</option>)}
              </Select>
              <Btn variant="outline" size="sm" icon={Download}>Export PDF</Btn>
            </div>
          }
        />

        {/* KPI row */}
        <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
          <StatCard label="Total Applicants" value="87" icon={Users} color={C.accent} delta={12} />
          <StatCard label="Shortlisted" value="60" icon={CheckCircle} color={C.success} delta={8} />
          <StatCard label="Rejected" value="27" icon={X} color={C.danger} delta={-3} />
          <StatCard label="Invitations Sent" value="45" icon={Send} color={C.warn} delta={5} />
          <StatCard label="Accepted" value="38" icon={Award} color={C.accent2} delta={10} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
          {/* Application trend */}
          <Card style={{ padding: 24 }}>
            <SectionHeader title="Application Trend" sub="Weekly registrations" />
            <BarChart data={trendData} color={C.accent} height={100} />
          </Card>

          {/* School type distribution */}
          <Card style={{ padding: 24 }}>
            <SectionHeader title="School Categories" />
            <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
              <DonutChart segments={typeData} size={120} label={{ value: "87", sub: "Total" }} />
              <div style={{ flex: 1 }}>
                {typeData.map((t, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 3, background: t.color }} />
                        <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{t.label}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{t.value}</span>
                    </div>
                    <ProgressBar value={(t.value / 87) * 100} color={t.color} height={5} />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
          {/* Acceptance rate funnel */}
          <Card style={{ padding: 24 }}>
            <SectionHeader title="Conversion Funnel" sub="Application to acceptance" />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {funnelSteps.map((f, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{f.label}</span>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: f.color }}>{f.value}</span>
                      <span style={{ fontSize: 11, color: C.muted, minWidth: 36, textAlign: "right" }}>{f.pct}%</span>
                    </div>
                  </div>
                  <ProgressBar value={f.pct} color={f.color} />
                </div>
              ))}
            </div>
          </Card>

          {/* Geography */}
          <Card style={{ padding: 24 }}>
            <SectionHeader title="Applicant Geography" />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {geoData.map((g, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>
                        {g.country === "India" ? "🇮🇳" : g.country === "UAE" ? "🇦🇪" : g.country === "UK" ? "🇬🇧" : g.country === "Singapore" ? "🇸🇬" : "🇦🇺"}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{g.country}</span>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{g.count}</span>
                      <span style={{ fontSize: 11, color: C.muted, minWidth: 34, textAlign: "right" }}>{g.pct}%</span>
                    </div>
                  </div>
                  <ProgressBar value={g.pct} color={C.accent} />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Top participating schools */}
        <Card style={{ overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text }}>Top Participating Schools</h3>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.light }}>
                {["#", "School Name", "Country", "Events Attended", "Status"].map(h => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topSchools.map((s, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: "12px 16px", fontWeight: 800, color: i < 3 ? C.warn : C.muted, fontSize: 14 }}>
                    {i < 3 ? "🏆" : i + 1}
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 700, fontSize: 13, color: C.text }}>{s.name}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: C.muted }}>{s.country}</td>
                  <td style={{ padding: "12px 16px", fontWeight: 800, fontSize: 15, color: C.accent }}>{s.events}</td>
                  <td style={{ padding: "12px 16px" }}><Badge status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </PageWrap>
    </AppShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SCREEN 14 – SYSTEM TIMELINE VIEW
// ══════════════════════════════════════════════════════════════════════════
export function SystemTimeline() {
  const uc = { ...getUserCredentials(), role: "event_creator" };

  const events = [
    {
      id: 1,
      name: "Global EdTech Summit 2025",
      status: "registration_open",
      color: "#1e3a5f",
      phases: [
        { label: "Event Created", date: "2025-07-01", status: "done", desc: "Event published by creator" },
        { label: "Registration Open", date: "2025-08-01", status: "done", desc: "Schools can now apply" },
        { label: "Registration Close", date: "2025-10-01", status: "active", desc: "Deadline for applications" },
        { label: "Applicant Review", date: "2025-10-05 – 10-15", status: "upcoming", desc: "Review & shortlist applicants" },
        { label: "Invitation Release", date: "2025-10-20", status: "upcoming", desc: "Send invitations to shortlisted" },
        { label: "Acceptance Deadline", date: "2025-11-01", status: "upcoming", desc: "Schools confirm attendance" },
        { label: "Event Day", date: "2025-11-15", status: "upcoming", desc: "The main event" },
      ],
    },
    {
      id: 2,
      name: "STEM Innovation Expo",
      status: "draft",
      color: "#8e44ad",
      phases: [
        { label: "Event Created", date: "2025-08-10", status: "done", desc: "Draft created" },
        { label: "Registration Open", date: "TBD", status: "upcoming", desc: "Not yet scheduled" },
        { label: "Registration Close", date: "TBD", status: "upcoming", desc: "" },
        { label: "Applicant Review", date: "TBD", status: "upcoming", desc: "" },
        { label: "Invitation Release", date: "TBD", status: "upcoming", desc: "" },
        { label: "Acceptance Deadline", date: "TBD", status: "upcoming", desc: "" },
        { label: "Event Day", date: "2025-12-05", status: "upcoming", desc: "" },
      ],
    },
    {
      id: 3,
      name: "School Leaders Forum",
      status: "invitations_released",
      color: "#17a589",
      phases: [
        { label: "Event Created", date: "2025-05-15", status: "done", desc: "" },
        { label: "Registration Open", date: "2025-06-01", status: "done", desc: "" },
        { label: "Registration Close", date: "2025-07-15", status: "done", desc: "" },
        { label: "Applicant Review", date: "2025-07-16 – 07-31", status: "done", desc: "" },
        { label: "Invitation Release", date: "2025-08-05", status: "done", desc: "60 invitations sent" },
        { label: "Acceptance Deadline", date: "2025-09-01", status: "active", desc: "38 accepted so far" },
        { label: "Event Day", date: "2026-01-20", status: "upcoming", desc: "" },
      ],
    },
  ];

  const phaseColors = {
    done:     { bg: C.success,  text: "#fff", line: C.success },
    active:   { bg: C.accent,   text: "#fff", line: C.accent },
    upcoming: { bg: C.light,    text: C.muted, line: C.border },
  };

  const phaseIcons = {
    "Event Created":       Zap,
    "Registration Open":   CheckCircle,
    "Registration Close":  Clock,
    "Applicant Review":    Eye,
    "Invitation Release":  Send,
    "Acceptance Deadline": Bell,
    "Event Day":           Flag,
  };

  return (
    <AppShell>
      <PageWrap wide>
        <SectionHeader
          title="System Timeline View"
          sub={`Creator #${uc.user_id} · Entity #${uc.selectedentity} — Visual workflow for your events`}
        />

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
          {[{ label: "Completed", color: C.success }, { label: "In Progress", color: C.accent }, { label: "Upcoming", color: C.border }].map(l => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.muted }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: l.color }} />
              {l.label}
            </div>
          ))}
        </div>

        <SectionHeader title="Event Lifecycle Timelines" sub={`${events.length} events · Showing all phases`} />

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {events.map(ev => (
            <Card key={ev.id} style={{ overflow: "hidden" }}>
              {/* Event header */}
              <div style={{ padding: "16px 24px", background: `linear-gradient(135deg, ${ev.color}, ${ev.color}cc)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, background: "rgba(255,255,255,.2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900 }}>
                    {ev.name[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: "#fff" }}>{ev.name}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,.75)" }}>Event #{ev.id} · Creator #{uc.user_id}</div>
                  </div>
                </div>
                <Badge status={ev.status} />
              </div>

              {/* Timeline */}
              <div style={{ padding: "28px 24px 24px" }}>
                {/* Desktop timeline (horizontal) */}
                <div style={{ position: "relative", paddingBottom: 8 }}>
                  {/* Connector line */}
                  <div style={{ position: "absolute", top: 18, left: "calc(100% / 14)", right: "calc(100% / 14)", height: 3, background: C.light, zIndex: 0 }} />
                  {/* Progress line */}
                  <div style={{
                    position: "absolute", top: 18,
                    left: "calc(100% / 14)",
                    width: `${((ev.phases.filter(p => p.status === "done").length) / (ev.phases.length - 1)) * (100 - 100 / 7)}%`,
                    height: 3, background: `linear-gradient(90deg, ${C.success}, ${C.accent})`, zIndex: 1,
                  }} />

                  <div style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
                    {ev.phases.map((phase, i) => {
                      const col = phaseColors[phase.status];
                      const PhaseIcon = phaseIcons[phase.label] || CheckCircle;
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          {/* Circle */}
                          <div style={{
                            width: 36, height: 36, borderRadius: "50%",
                            background: col.bg, border: `3px solid ${phase.status === "active" ? C.accent : col.bg}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: phase.status === "active" ? `0 0 0 5px ${C.accent}30` : "none",
                            transition: "all .3s",
                            cursor: "pointer",
                          }}>
                            <PhaseIcon size={14} color={col.text} />
                          </div>

                          {/* Label */}
                          <div style={{ textAlign: "center", maxWidth: 90 }}>
                            <div style={{
                              fontSize: 11, fontWeight: 800, color: phase.status === "done" ? C.success : phase.status === "active" ? C.accent : C.muted,
                              lineHeight: 1.3, marginBottom: 2,
                            }}>{phase.label}</div>
                            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.3 }}>{phase.date}</div>
                            {phase.desc && <div style={{ fontSize: 10, color: C.muted, marginTop: 2, fontStyle: "italic", lineHeight: 1.3 }}>{phase.desc}</div>}
                          </div>

                          {/* Status pill */}
                          {phase.status !== "upcoming" && (
                            <div style={{
                              padding: "2px 8px", borderRadius: 99, fontSize: 9, fontWeight: 800,
                              background: phase.status === "done" ? C.success + "20" : C.accent + "20",
                              color: phase.status === "done" ? C.success : C.accent,
                              textTransform: "uppercase", letterSpacing: .4,
                            }}>
                              {phase.status === "done" ? "✓ Done" : "● Live"}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Progress summary */}
                <div style={{ marginTop: 20, padding: "12px 16px", background: C.light, borderRadius: 8, display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: C.text }}>Overall Progress</span>
                      <span style={{ fontWeight: 800, color: C.accent }}>
                        {ev.phases.filter(p => p.status === "done").length}/{ev.phases.length} phases complete
                      </span>
                    </div>
                    <ProgressBar value={(ev.phases.filter(p => p.status === "done").length / ev.phases.length) * 100} color={`linear-gradient(90deg, ${C.success}, ${C.accent})`} height={8} />
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    {[
                      { label: "Completed", count: ev.phases.filter(p => p.status === "done").length, color: C.success },
                      { label: "Active", count: ev.phases.filter(p => p.status === "active").length, color: C.accent },
                      { label: "Upcoming", count: ev.phases.filter(p => p.status === "upcoming").length, color: C.muted },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: s.color }}>{s.count}</div>
                        <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Legend card */}
        <Card style={{ padding: 20, marginTop: 24 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 14 }}>Phase Reference Guide</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
            {[
              { phase: "Event Created", desc: "Event is set up by the creator", icon: Zap, color: C.success },
              { phase: "Registration Open", desc: "Schools can start applying", icon: CheckCircle, color: C.success },
              { phase: "Registration Close", desc: "Application window ends", icon: Clock, color: C.warn },
              { phase: "Applicant Review", desc: "Creator reviews all profiles", icon: Eye, color: C.accent },
              { phase: "Invitation Release", desc: "Invitations sent to shortlist", icon: Send, color: C.purple },
              { phase: "Acceptance Deadline", desc: "Schools confirm attendance", icon: Bell, color: C.warn },
              { phase: "Event Day", desc: "The main event takes place", icon: Flag, color: C.danger },
            ].map(item => (
              <div key={item.phase} style={{ display: "flex", gap: 10, padding: "10px 12px", background: C.bg, borderRadius: 8, alignItems: "flex-start" }}>
                <div style={{ background: item.color + "20", borderRadius: 8, padding: 6, flexShrink: 0 }}>
                  <item.icon size={14} color={item.color} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{item.phase}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </PageWrap>
    </AppShell>
  );
}
